import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Typography,
  Skeleton,
  Card,
  CardContent,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Fab,
  Zoom,
  Tooltip,
  TextField,
  Avatar,
  Chip,
  IconButton,
  Autocomplete,
  CircularProgress
} from '@mui/material';
import Grid from '@mui/material/Grid';
import {
  Add as AddIcon,
  Groups as GroupsIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Person as PersonIcon,
  Badge as BadgeIcon
} from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import useOrganizationSetupStore from '@/store/OrganizationSetupStore';
import { Team, AvailableManager } from '@/services/OrganizationSetupService';

interface TeamsSectionProps {
  canEdit: boolean;
}

const TeamsSection: React.FC<TeamsSectionProps> = ({ canEdit }) => {
  const theme = useTheme();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [editingTeam, setEditingTeam] = useState<Team | null>(null);
  const [teamToDelete, setTeamToDelete] = useState<Team | null>(null);

  // Form state
  const [teamName, setTeamName] = useState('');
  const [description, setDescription] = useState('');
  const [selectedManager, setSelectedManager] = useState<AvailableManager | null>(null);

  const {
    teams,
    teamsLoading,
    loading,
    allEmployeesForManager,
    fetchTeams,
    fetchAllEmployeesForManager,
    addTeam,
    editTeam,
    removeTeam
  } = useOrganizationSetupStore();

  useEffect(() => {
    if (canEdit && dialogOpen) {
      fetchAllEmployeesForManager();
    }
  }, [canEdit, dialogOpen, fetchAllEmployeesForManager]);

  const handleOpenDialog = (team?: Team) => {
    if (team) {
      setEditingTeam(team);
      setTeamName(team.team_name);
      setDescription(team.description || '');
      // Find the current manager in allEmployeesForManager
      if (team.manager_id) {
        const currentManager = allEmployeesForManager.find(e => e.id === team.manager_id);
        setSelectedManager(currentManager || null);
      } else {
        setSelectedManager(null);
      }
    } else {
      setEditingTeam(null);
      setTeamName('');
      setDescription('');
      setSelectedManager(null);
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingTeam(null);
    setTeamName('');
    setDescription('');
    setSelectedManager(null);
  };

  const handleSave = async () => {
    const data = {
      team_name: teamName,
      description: description || undefined,
      manager_id: selectedManager?.id || undefined
    };

    let success = false;
    if (editingTeam) {
      success = await editTeam(editingTeam.id, data);
    } else {
      success = await addTeam(data);
    }

    if (success) {
      handleCloseDialog();
    }
  };

  const handleDeleteClick = (team: Team) => {
    setTeamToDelete(team);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    if (teamToDelete) {
      await removeTeam(teamToDelete.id);
      setDeleteDialogOpen(false);
      setTeamToDelete(null);
    }
  };

  // Check if employee already manages another team (for validation message)
  const getManagerWarning = (employee: AvailableManager): string | null => {
    if (employee.manages_team_id && employee.manages_team_id !== editingTeam?.id) {
      return `Already manages: ${employee.manages_team_name}`;
    }
    return null;
  };

  // Loading State
  if (teamsLoading && teams.length === 0) {
    return (
      <Box>
        <Grid container spacing={3}>
          {[1, 2, 3].map(i => (
            <Grid size={{ xs: 12, sm: 6, md: 4 }} key={i}>
              <Card elevation={0} sx={{ borderRadius: 3, border: `1px solid ${theme.palette.divider}` }}>
                <CardContent sx={{ p: 3 }}>
                  <Skeleton variant="text" width="60%" height={30} />
                  <Skeleton variant="text" width="80%" height={20} sx={{ mt: 1 }} />
                  <Box sx={{ display: 'flex', alignItems: 'center', mt: 2 }}>
                    <Skeleton variant="circular" width={40} height={40} />
                    <Box sx={{ ml: 2 }}>
                      <Skeleton variant="text" width={100} height={20} />
                      <Skeleton variant="text" width={80} height={16} />
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Box>
    );
  }

  // Empty State
  if (teams.length === 0) {
    return (
      <Box>
        <Card
          elevation={0}
          sx={{
            borderRadius: 3,
            border: `2px dashed ${theme.palette.divider}`,
            p: 6,
            textAlign: 'center'
          }}
        >
          <Box
            sx={{
              width: 100,
              height: 100,
              borderRadius: '50%',
              backgroundColor: theme.palette.primary.main + '15',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mx: 'auto',
              mb: 3
            }}
          >
            <GroupsIcon sx={{ fontSize: 48, color: theme.palette.primary.main }} />
          </Box>
          <Typography variant="h5" fontWeight={600} gutterBottom>
            No Teams Yet
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 4, maxWidth: 400, mx: 'auto' }}>
            Start building your organization's team structure by adding departments and assigning managers.
          </Typography>
          {canEdit && (
            <Button
              variant="contained"
              size="large"
              startIcon={<AddIcon />}
              onClick={() => handleOpenDialog()}
              sx={{ borderRadius: 2, px: 4 }}
            >
              Add First Team
            </Button>
          )}
        </Card>
      </Box>
    );
  }

  // Main Content
  return (
    <Box>
      {/* Header with Add Button */}
      {canEdit && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 3 }}>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog()}
            sx={{ borderRadius: 2 }}
          >
            Add Team
          </Button>
        </Box>
      )}

      {/* Teams Grid */}
      <Grid container spacing={3}>
        {teams.map(team => (
          <Grid size={{ xs: 12, sm: 6, md: 4 }} key={team.id}>
            <Card
              elevation={0}
              sx={{
                borderRadius: 3,
                border: `1px solid ${theme.palette.divider}`,
                height: '100%',
                transition: 'all 0.2s ease-in-out',
                '&:hover': {
                  borderColor: theme.palette.primary.main,
                  boxShadow: `0 4px 20px ${theme.palette.primary.main}20`
                }
              }}
            >
              <CardContent sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                  <Box>
                    <Typography variant="h6" fontWeight={600}>
                      {team.team_name}
                    </Typography>
                    {team.description && (
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                        {team.description}
                      </Typography>
                    )}
                  </Box>
                  {canEdit && (
                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                      <IconButton
                        size="small"
                        onClick={() => handleOpenDialog(team)}
                        sx={{ color: theme.palette.text.secondary }}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => handleDeleteClick(team)}
                        sx={{ color: theme.palette.error.main }}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  )}
                </Box>

                {/* Manager Section */}
                <Box sx={{ mt: 2 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                    Manager
                  </Typography>
                  {team.manager_id ? (
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Avatar
                        src={team.manager_photo}
                        sx={{ width: 40, height: 40, bgcolor: theme.palette.primary.main }}
                      >
                        {team.manager_name?.charAt(0)}
                      </Avatar>
                      <Box sx={{ ml: 1.5 }}>
                        <Typography variant="body2" fontWeight={500}>
                          {team.manager_name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {team.manager_email}
                        </Typography>
                      </Box>
                    </Box>
                  ) : (
                    <Box sx={{ display: 'flex', alignItems: 'center', color: theme.palette.text.disabled }}>
                      <PersonIcon sx={{ mr: 1 }} />
                      <Typography variant="body2">No manager assigned</Typography>
                    </Box>
                  )}
                </Box>

                {/* Member Count */}
                <Box sx={{ mt: 2, pt: 2, borderTop: `1px solid ${theme.palette.divider}` }}>
                  <Chip
                    icon={<GroupsIcon />}
                    label={`${team.member_count} member${team.member_count !== 1 ? 's' : ''}`}
                    size="small"
                    variant="outlined"
                    sx={{ borderRadius: 2 }}
                  />
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Floating Action Button (Mobile) */}
      {canEdit && (
        <Zoom in>
          <Tooltip title="Add Team">
            <Fab
              color="primary"
              onClick={() => handleOpenDialog()}
              sx={{
                position: 'fixed',
                bottom: 32,
                right: 32,
                display: { xs: 'flex', md: 'none' }
              }}
            >
              <AddIcon />
            </Fab>
          </Tooltip>
        </Zoom>
      )}

      {/* Add/Edit Team Dialog */}
      <Dialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle>
          {editingTeam ? 'Edit Team' : 'Add New Team'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            <TextField
              label="Team Name"
              value={teamName}
              onChange={(e) => setTeamName(e.target.value)}
              fullWidth
              required
              sx={{ mb: 2 }}
            />
            <TextField
              label="Description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              fullWidth
              multiline
              rows={3}
              sx={{ mb: 2 }}
            />
            <Autocomplete
              value={selectedManager}
              onChange={(_, newValue) => setSelectedManager(newValue)}
              options={allEmployeesForManager}
              getOptionLabel={(option) => option.name}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              renderOption={(props, option) => {
                const warning = getManagerWarning(option);
                return (
                  <Box component="li" {...props} sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Avatar src={option.profile_photo} sx={{ width: 32, height: 32 }}>
                      {option.name?.charAt(0)}
                    </Avatar>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="body2">{option.name}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {option.role_name} {option.current_department ? `- ${option.current_department}` : ''}
                      </Typography>
                      {warning && (
                        <Typography variant="caption" color="warning.main" sx={{ display: 'block' }}>
                          {warning}
                        </Typography>
                      )}
                    </Box>
                    {option.manages_team_id && option.manages_team_id !== editingTeam?.id && (
                      <BadgeIcon color="warning" fontSize="small" />
                    )}
                  </Box>
                );
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Manager"
                  placeholder="Select a manager"
                  helperText="One employee can manage only one team"
                />
              )}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2.5 }}>
          <Button
            onClick={handleCloseDialog}
            variant="outlined"
            sx={{ borderRadius: 2 }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            variant="contained"
            disabled={!teamName.trim() || loading}
            sx={{ borderRadius: 2 }}
          >
            {loading ? <CircularProgress size={24} /> : editingTeam ? 'Save Changes' : 'Add Team'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle>Delete Team</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete the team{' '}
            <strong>{teamToDelete?.team_name}</strong>?
            <br /><br />
            This will remove the manager assignment and unassign all team members.
            This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ p: 2.5 }}>
          <Button
            onClick={() => setDeleteDialogOpen(false)}
            variant="outlined"
            sx={{ borderRadius: 2 }}
          >
            Cancel
          </Button>
          <Button
            onClick={confirmDelete}
            variant="contained"
            color="error"
            disabled={loading}
            sx={{ borderRadius: 2 }}
          >
            {loading ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TeamsSection;
